import type { GoogleStatusResponse } from "@/api/types";

/**
 * Settings shell feature gates — single source of truth for the two rollback /
 * safety switches the TZ requires. Both are plain consts so a revert is a
 * one-line change (TZ W2 / W3).
 */

/**
 * W2 — Settings route + nav exposure. Flip to `false` to instantly hide the
 * Settings nav-item and revert the UserMenu profile link back to the crmold
 * Espo profile (`/#User/view/me`). The /settings route itself stays mounted
 * (harmless) but becomes unreachable from the UI.
 */
export const SETTINGS_ROUTE_ENABLED = true;

/**
 * W3 — destructive `disconnect_google` (POST /oauth/google/revoke) soft-gate.
 *
 * Kept `false` until the revoke behaviour (token wipe, reconnect, calendar
 * jobs stop) is verified on staging per TZ §6. While closed the Settings page
 * shows connection status ONLY and hides the destructive "повне відключення"
 * button — no half-wired destructive action ever reaches a real user.
 *
 * There is also NO backend endpoint to toggle `isCalendarSyncEnabled` on its
 * own (verified: only /oauth/google/revoke writes), so a standalone
 * "призупинити синхронізацію" control is NOT wired either — sync state is
 * rendered read-only.
 */
export const DISCONNECT_GOOGLE_ENABLED = false;

/** Static profile language — `/me` does not expose a locale field (sub_0). */
export const STATIC_LANGUAGE_LABEL = "Українська (uk_UA)";

// ── Connection-state derivation (TZ §0 / SET-11) ──
// Derived ONLY from connectionStatus + canEnableCalendar. token_expired is
// folded into refresh_failed by the backend; disconnectedReason / tokenExpiresAt
// are not surfaced. We never show a token reason.

export type CalendarConnectionState =
  | "connected"
  | "partial_scope"
  | "refresh_failed"
  | "disconnected";

export function deriveCalendarState(s: GoogleStatusResponse): CalendarConnectionState {
  const status = s.connectionStatus;
  if (status === "refresh_failed") return "refresh_failed";
  if (status === "connected" && s.connected) {
    // connected but no usable calendar scope → partial grant.
    return s.canEnableCalendar ? "connected" : "partial_scope";
  }
  // disconnected | revoked | anything-else → treat as disconnected.
  return "disconnected";
}

/** A reconnect is needed for everything except a fully-scoped connection. */
export function needsReconnect(state: CalendarConnectionState): boolean {
  return state !== "connected";
}

/**
 * Derived role label (TZ SET-02) — `/me` has no single roleLabel/tier field,
 * so we derive it from roles[] + type. admin-type wins the label; otherwise
 * the assigned role names; falling back to a generic "Користувач".
 */
export function deriveRoleLabel(roles: string[], type?: string): string {
  const named = roles.filter((r) => r && r.trim() !== "");
  if (type === "admin" || type === "super-admin") {
    return named.length > 0 ? `Адміністратор · ${named.join(", ")}` : "Адміністратор";
  }
  if (named.length > 0) return named.join(", ");
  return "Користувач";
}

/** Human-readable scope labels (calendar only — Gmail is never tracked). */
export function scopeLabel(scope: string): string {
  switch (scope) {
    case "calendar.events.readonly":
      return "Календар — читання подій";
    case "calendar.events.owned":
      return "Календар — власні події";
    default:
      return scope;
  }
}
